import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { acquisitionsApi, type PurchaseOrder, type Vendor } from "@/lib/api/acquisitions";
import { downloadCsv } from "@/lib/csv-export";
import { ShoppingCart, Plus, Download } from "lucide-react";

const poStatusMap: Record<
  string,
  { variant: "success" | "warning" | "info" | "danger" | "neutral" | "purple"; label: string }
> = {
  DRAFT: { variant: "neutral", label: "Draft" },
  SENT: { variant: "info", label: "Sent" },
  PARTIALLY_RECEIVED: { variant: "warning", label: "Partially Received" },
  FULLY_RECEIVED: { variant: "success", label: "Fully Received" },
  INVOICED: { variant: "purple", label: "Invoiced" },
  CLOSED: { variant: "neutral", label: "Closed" },
  CANCELLED: { variant: "danger", label: "Cancelled" },
};

export default function PurchaseOrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vendorFromQuery = searchParams.get("vendorId");
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [vendorFilter, setVendorFilter] = useState<string>(vendorFromQuery || "all");
  const [vendors, setVendors] = useState<Vendor[]>([]);

  useEffect(() => {
    if (vendorFromQuery) setVendorFilter(vendorFromQuery);
  }, [vendorFromQuery]);

  useEffect(() => {
    acquisitionsApi.vendors
      .list({ isActive: true, limit: 300 })
      .then((r) => setVendors(r.vendors))
      .catch(() => setVendors([]));
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await acquisitionsApi.orders.list({
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        vendorId: vendorFilter !== "all" ? vendorFilter : undefined,
        limit: 50,
      });
      setOrders(res.orders);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to load orders", err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, vendorFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const exportCsv = () => {
    const headers = ["ID", "PO Number", "Vendor", "Lines", "Shipments", "Status", "Created"];
    const rows = orders.map((o) => {
      const cfg = poStatusMap[o.status] || { label: o.status };
      return [
        o.id,
        o.orderNumber,
        o.vendor.name,
        String(o._count?.lines ?? 0),
        String(o._count?.shipments ?? 0),
        cfg.label,
        new Date(o.createdAt).toISOString(),
      ];
    });
    downloadCsv(`purchase-orders-${new Date().toISOString().slice(0, 10)}`, headers, rows);
  };

  const columns: DataTableColumn<PurchaseOrder>[] = [
    {
      id: "orderNumber",
      header: "PO Number",
      cell: (row) => (
        <button
          onClick={() => navigate(`/acquisitions/orders/${row.id}`)}
          className="font-mono font-medium text-primary hover:underline"
        >
          {row.orderNumber}
        </button>
      ),
    },
    {
      id: "vendor",
      header: "Vendor",
      cell: (row) => <span className="text-sm text-slate-700">{row.vendor.name}</span>,
    },
    {
      id: "lines",
      header: "Lines",
      cell: (row) => <span className="text-sm tabular-nums">{row._count?.lines || 0}</span>,
      width: "w-16",
      align: "center",
      hideOnMobile: true,
    },
    {
      id: "shipments",
      header: "Shipments",
      cell: (row) => <span className="text-sm tabular-nums">{row._count?.shipments || 0}</span>,
      width: "w-20",
      align: "center",
      hideOnMobile: true,
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => {
        const cfg = poStatusMap[row.status] || { variant: "neutral" as const, label: row.status };
        return <StatusBadge variant={cfg.variant}>{cfg.label}</StatusBadge>;
      },
      width: "w-36",
    },
    {
      id: "date",
      header: "Created",
      cell: (row) => (
        <span className="text-xs text-slate-500">
          {new Date(row.createdAt).toLocaleDateString()}
        </span>
      ),
      width: "w-24",
      hideOnMobile: true,
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Purchase Orders"
        description="Manage vendor orders and track shipments"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "Purchase Orders" }]}
        titleSize="md"
        searchQuery={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by PO number..."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={loading || orders.length === 0}
              onClick={exportCsv}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button onClick={() => navigate("/acquisitions/orders/new")}>
              <Plus className="mr-2 h-4 w-4" />
              New purchase order
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(poStatusMap).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={vendorFilter} onValueChange={setVendorFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Vendor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All vendors</SelectItem>
            {vendors.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.name} ({v.code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">
          {total} order{total !== 1 ? "s" : ""}
        </span>
      </div>

      <DataTable
        columns={columns}
        data={orders}
        rowKey={(row) => row.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<ShoppingCart className="h-6 w-6 text-slate-400" />}
            title="No purchase orders yet"
            description="Create a purchase order from the toolbar, or open an approved request and use Create purchase order there."
            action={
              <Button onClick={() => navigate("/acquisitions/orders/new")}>
                <Plus className="mr-2 h-4 w-4" />
                New purchase order
              </Button>
            }
          />
        }
      />
    </PageContainer>
  );
}

export { poStatusMap };
